import { useState, useMemo, useRef, useEffect } from 'react';
import { useSelector } from '@legendapp/state/react';
import { auth$ } from '@/stores/auth$';
import { ui$, setActiveNoteId, toggleNoteSelection, toggleShowArchived, setSearchTerm } from '@/stores/ui$';
import { createNote, createSkillNote, deleteNote, togglePin, getNotesArray, getActiveNotes, getArchivedNotes, archiveNote, unarchiveNote, restoreNote, permanentlyDeleteNote, isSmartTag, isSkillNote, SMART_TAG_PREFIX } from '@/stores/notes$';
import { snippets$ } from '@/stores/snippets$';
import { getSharedTagNotes, isTagShared, sharedTagPaths$ } from '@/stores/sharedTags$';
import { pin$, isNoteUnlocked } from '@/stores/pin$';
import { cn } from '@/lib/utils';
import { Plus, Search, Clock, Trash2, CheckSquare, X, Pin, Archive, ArchiveRestore, EyeOff, Eye, Users, Lock, LockOpen, FileText, Star, MoreVertical, Flag, Puzzle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { trackEvent } from '@/lib/analytics';
import ReportContentModal from './ReportContentModal';

export default function NoteList() {
  const user = useSelector(() => auth$.user.get());
  const activeNoteId = useSelector(() => ui$.activeNoteId.get());
  const viewMode = useSelector(() => ui$.viewMode.get());
  const selectedNoteIds = useSelector(() => ui$.selectedNoteIds.get());
  const selectedTagFilter = useSelector(() => ui$.selectedTagFilter.get());
  const showArchived = useSelector(() => ui$.showArchived.get());
  const searchTerm = useSelector(() => ui$.searchTerm.get());
  const smartTagsEnabled = useSelector(() => ui$.smartTagsEnabled.get());
  const selectedSharedTag = useSelector(() => ui$.selectedSharedTag.get());

  // Shared tags reactivity (for blue shared tag styling in note cards)
  useSelector(() => sharedTagPaths$.get());

  // Shared notes state
  const [sharedNotes, setSharedNotes] = useState([]);
  const [sharedNotesLoading, setSharedNotesLoading] = useState(false);
  const isSharedView = !!selectedSharedTag;

  // Report state for shared tag notes
  const [reportNoteId, setReportNoteId] = useState(null);
  const [reportMenuNoteId, setReportMenuNoteId] = useState(null);

  // Fetch shared notes when selectedSharedTag changes
  useEffect(() => {
    if (selectedSharedTag?.id) {
      setSharedNotesLoading(true);
      getSharedTagNotes(selectedSharedTag.id).then((notes) => {
        setSharedNotes(notes);
        setSharedNotesLoading(false);
      });
    } else {
      setSharedNotes([]);
    }
  }, [selectedSharedTag?.id]);

  // Get notes with snippets
  const notesWithSnippets = useSelector(() => {
    const notes = getNotesArray();
    const snippetsObj = snippets$.get();

    // If snippetsObj is not an object, return notes without snippets
    const snippetsArray = snippetsObj && typeof snippetsObj === 'object' && !Array.isArray(snippetsObj)
      ? Object.values(snippetsObj)
      : [];

    return notes.map(note => ({
      ...note,
      snippets: snippetsArray
        .filter(s => s && typeof s === 'object' && s.note_id === note.id && !s.deleted)
        .sort((a, b) => (a.version || 0) - (b.version || 0)),
    }));
  });

  const [isSelectionMode, setIsSelectionMode] = useState(false);

  // Debounced search: local state for immediate input feedback, debounce store updates
  const [localSearchValue, setLocalSearchValue] = useState(searchTerm);
  const debounceTimerRef = useRef(null);

  // Sync local value when store changes externally (e.g., cleared from elsewhere)
  useEffect(() => {
    setLocalSearchValue(searchTerm);
  }, [searchTerm]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearchValue(value); // Immediate visual feedback

    // Debounce the actual filter update (150ms)
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setSearchTerm(value);
      if (value.trim()) {
        trackEvent('search_performed', { query_length: value.trim().length });
      }
    }, 150);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    if (isSelectionMode) {
      ui$.selectedNoteIds.set([]); // Clear selection when exiting
    }
  };

  const handleAddNote = () => {
    if (user) {
      if (viewMode === 'skills') {
        // In skills view, create a skill note instead
        const name = prompt('Skill name (e.g. "my-skill"):');
        if (name && name.trim()) {
          createSkillNote(name.trim(), user.id, (noteId) => setActiveNoteId(noteId));
        }
        return;
      }
      // Auto-tag if a tag filter is active
      const autoTag = (selectedTagFilter && selectedTagFilter !== '__untagged__') ? selectedTagFilter : null;
      createNote('Untitled Note', user.id, (noteId) => setActiveNoteId(noteId), autoTag);
    }
  };

  const handleBulkDelete = () => {
    selectedNoteIds.forEach(noteId => {
      deleteNote(noteId, (deletedId) => {
        if (activeNoteId === deletedId) {
          setActiveNoteId(null);
        }
      });
    });
    ui$.selectedNoteIds.set([]);
    setIsSelectionMode(false);
  };

  const handleTogglePin = (noteId) => {
    togglePin(noteId);
  };

  // Filter and sort notes based on viewMode and tag filter
  const filteredAndSortedNotes = useMemo(() => {
    let filtered = notesWithSnippets;

    // STEP 1: Apply view mode / tag filters first to get base set
    if (selectedTagFilter) {
      // Tag view mode - show all notes matching this tag
      if (selectedTagFilter === '__untagged__') {
        // Special filter for untagged notes
        filtered = filtered.filter(note => !note.tags || note.tags.length === 0);
      } else {
        // Normal tag filter (includes hierarchical matching)
        filtered = filtered.filter(note =>
          note.tags?.some(tag =>
            tag === selectedTagFilter || tag.startsWith(selectedTagFilter + '/')
          )
        );
      }
      // Respect showArchived toggle in tag view
      filtered = showArchived ? filtered : filtered.filter(note => !note.archived);
    } else {
      // Regular view modes (no tag filtering)
      switch (viewMode) {
        case 'favorites':
          // Show notes that have at least one favorite snippet
          filtered = filtered.filter(note =>
            note.snippets.some(snippet => snippet.favorite)
          );
          // Respect showArchived toggle in favorites view
          filtered = showArchived ? filtered : filtered.filter(note => !note.archived);
          break;
        case 'skills':
          // Show only skill notes
          filtered = filtered.filter(note => note.note_type === 'skill');
          filtered = showArchived ? filtered : filtered.filter(note => !note.archived);
          break;
        case 'archive':
          // Show only archived notes
          filtered = filtered.filter(note => note.archived);
          break;
        case 'trash':
          // Show only deleted notes (soft deleted)
          filtered = []; // Trash notes are filtered out by getNotesArray, handled separately
          break;
        case 'notes':
        default:
          // Show only regular notes — exclude skills which have their own dedicated tab
          // Treat null/undefined note_type as 'note' for backward compatibility
          filtered = filtered.filter(note => !note.note_type || note.note_type === 'note');
          filtered = showArchived ? filtered : filtered.filter(note => !note.archived);
          break;
      }
    }

    // STEP 2: Apply search filter on top of the filtered set
    // Fuzzy search: split search term into words, match if ALL words are found in title OR any snippet title/content
    if (searchTerm && searchTerm.trim() !== '') {
      const searchWords = searchTerm.toLowerCase().trim().split(/\s+/).filter(w => w.length > 0);

      filtered = filtered.filter(note => {
        // Build searchable text from note title and all snippet titles + contents
        const titleText = (note.title || '').toLowerCase();
        const snippetTexts = note.snippets.flatMap(s => [
          (s.title || '').toLowerCase(),
          (s.content || '').toLowerCase()
        ]);
        const allText = [titleText, ...snippetTexts].join(' ');

        // Check if ALL search words are found somewhere in the combined text
        return searchWords.every(word => allText.includes(word));
      });
    }

    // Separate active and archived notes for proper display order
    const activeNotes = filtered.filter(note => !note.archived);
    const archivedNotes = filtered.filter(note => note.archived);

    // Sort function
    const sortByPinnedAndDate = (notes) => {
      return notes.sort((a, b) => {
        const aIsPinned = a.pinned;
        const bIsPinned = b.pinned;

        // Pinned notes always come first
        if (aIsPinned && !bIsPinned) return -1;
        if (!aIsPinned && bIsPinned) return 1;

        // Among pinned (or unpinned), sort by updatedAt desc
        return new Date(b.updated_at) - new Date(a.updated_at);
      });
    };

    // Sort both groups separately
    const sortedActive = sortByPinnedAndDate([...activeNotes]);
    const sortedArchived = sortByPinnedAndDate([...archivedNotes]);

    // Combine: active first, then archived (in favorites, skills, and tag views)
    if (viewMode === 'favorites' || viewMode === 'skills' || selectedTagFilter) {
      return [...sortedActive, ...sortedArchived];
    }

    // For other views, just return the filtered and sorted notes
    return sortByPinnedAndDate([...filtered]);
  }, [notesWithSnippets, viewMode, selectedTagFilter, showArchived, searchTerm]);

  return (
    <div className={cn(
      "w-full md:w-[300px] border-r border-border h-full min-h-0 flex-col bg-background/50 backdrop-blur-xl relative pl-[var(--sal)]",
      // Display MUST stay flex (not block) or the scroll child stops being a flex
      // item and grows to content height instead of scrolling. Mobile: hide when a
      // note is active (show editor instead); desktop: always flex.
      activeNoteId ? "hidden md:flex" : "flex",
      // Mobile: top padding handled by App.jsx wrapper
    )}>
      <div className="p-3 border-b border-border/50 bg-background/95 z-10 backdrop-blur-sm">
        {/* Shared view header */}
        {isSharedView && (
          <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
            <Users size={12} />
            <span>Shared: <span className="font-medium text-foreground">#{selectedSharedTag.tagPath}</span></span>
            <span className="text-muted-foreground/50">by {selectedSharedTag.ownerName}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
            {!isSharedView && (
              <button
                  onClick={toggleSelectionMode}
                  className={cn(
                      "h-9 w-9 flex items-center justify-center rounded-lg transition-all active:scale-95",
                      isSelectionMode ? "text-primary" : "text-muted-foreground/40 hover:text-muted-foreground"
                  )}
                  title={isSelectionMode ? "Exit Selection Mode" : "Select Notes"}
              >
                  {isSelectionMode ? <X size={18} /> : <CheckSquare size={18} />}
              </button>
            )}
            {!isSharedView && viewMode !== 'archive' && viewMode !== 'trash' && (
              <button
                  onClick={toggleShowArchived}
                  className={cn(
                      "h-9 w-9 flex items-center justify-center rounded-lg transition-all active:scale-95",
                      showArchived ? "text-primary" : "text-muted-foreground/40 hover:text-muted-foreground"
                  )}
                  title={showArchived ? "Hide archived notes" : "Show archived notes"}
              >
                  {showArchived ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            )}
            <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/70" />
                <input
                    type="text"
                    placeholder="Search..."
                    value={localSearchValue}
                    onChange={handleSearchChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setLocalSearchValue('');
                        setSearchTerm('');
                        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
                        e.target.blur();
                      }
                    }}
                    className="w-full h-9 pl-8 pr-8 bg-muted/40 hover:bg-muted/70 focus:bg-background border border-transparent focus:border-input rounded-lg text-sm transition-all focus:outline-none focus:ring-2 focus:ring-ring/20 placeholder:text-muted-foreground/50"
                />
                {localSearchValue && (
                  <button
                    type="button"
                    onClick={() => {
                      setLocalSearchValue('');
                      setSearchTerm('');
                      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 flex items-center justify-center text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
            </div>
            {!isSharedView && (
              <button
                  onClick={handleAddNote}
                  className="h-9 w-9 flex items-center justify-center rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-sm active:scale-95"
                  title={selectedTagFilter && selectedTagFilter !== '__untagged__' ? `New note in #${selectedTagFilter}` : 'New note'}
                  data-testid="create-note-button"
              >
                  <Plus size={18} />
              </button>
            )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {/* Shared view */}
        {isSharedView && (
          sharedNotesLoading ? (
            <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
              <p className="text-xs font-medium opacity-50">Loading shared notes...</p>
            </div>
          ) : sharedNotes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
              <p className="text-xs font-medium opacity-50">No shared notes yet</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {sharedNotes.map((note) => {
                const isOwn = note.isOwn;
                return (
                  <div key={note.id} className="relative group">
                    <div
                      onClick={() => {
                        if (isOwn) {
                          setActiveNoteId(note.id);
                          // Clear shared view read-only flag
                          ui$.viewingSharedNote.set(null);
                        } else {
                          // Set as read-only shared note
                          ui$.viewingSharedNote.set({
                            noteId: note.id,
                            authorName: note.author_username,
                            note,
                          });
                          setActiveNoteId(note.id);
                        }
                      }}
                      className={cn(
                        "w-full text-left p-4 hover:bg-muted/50 transition-all relative cursor-pointer",
                        activeNoteId === note.id ? "bg-muted" : ""
                      )}
                    >
                      {activeNoteId === note.id && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                      )}
                      {/* Author info */}
                      <div className="flex items-center gap-1.5 mb-1.5">
                        {note.author_avatar_url ? (
                          <img src={note.author_avatar_url} alt={note.author_username} className="w-4 h-4 rounded-full" />
                        ) : (
                          <div className="w-4 h-4 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[8px] font-medium">
                            {(note.author_username || 'U')[0].toUpperCase()}
                          </div>
                        )}
                        <span className="text-[10px] text-muted-foreground">{note.author_username}</span>
                        {isOwn && <span className="text-[9px] text-primary font-medium">(you)</span>}
                      </div>
                      <h3 className={cn("font-medium text-sm truncate mb-1", activeNoteId === note.id ? "text-primary dark:text-foreground" : "text-foreground")}>
                        {note.title || "Untitled Note"}
                      </h3>
                      <p className="text-xs text-muted-foreground line-clamp-2 mb-2 h-8 font-normal">
                        {note.snippets?.length > 0 ? note.snippets[0].content : "No content"}
                      </p>
                      <div className="flex items-center gap-1 text-[10px] text-muted-foreground/70">
                        <Clock size={10} />
                        <span>{formatDistanceToNow(new Date(note.updated_at), { addSuffix: true })}</span>
                      </div>
                    </div>
                    {/* Report menu for non-own notes */}
                    {!isOwn && (
                      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setReportMenuNoteId(reportMenuNoteId === note.id ? null : note.id);
                            }}
                            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                            title="More options"
                          >
                            <MoreVertical size={14} />
                          </button>
                          {reportMenuNoteId === note.id && (
                            <div className="absolute right-0 top-full mt-1 min-w-[160px] bg-popover border border-border rounded-lg shadow-lg z-50 overflow-hidden">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setReportMenuNoteId(null);
                                  setReportNoteId(note.id);
                                }}
                                className="flex items-center gap-2 w-full px-3 py-2 text-xs text-destructive hover:bg-destructive/10 transition-colors"
                              >
                                <Flag size={12} />
                                Report content
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}

        {/* Normal view */}
        {!isSharedView && (filteredAndSortedNotes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-muted-foreground gap-2">
                {viewMode === 'favorites' ? (
                  <>
                    <Star size={20} className="opacity-30" />
                    <p className="text-xs font-medium opacity-50">No favorite snippets</p>
                    <p className="text-[11px] opacity-40">Star snippets to find them here</p>
                  </>
                ) : (
                  <>
                    <FileText size={20} className="opacity-30" />
                    <p className="text-xs font-medium opacity-50">
                      {searchTerm ? 'No matching notes' : selectedTagFilter ? `No notes with this tag` : 'No notes yet'}
                    </p>
                    {!searchTerm && !selectedTagFilter && (
                      <button
                        onClick={handleAddNote}
                        className="mt-1 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 rounded-md transition-colors"
                      >
                        <Plus size={14} />
                        New Note
                      </button>
                    )}
                  </>
                )}
            </div>
        ) : (
            <div className="divide-y divide-border">
                {filteredAndSortedNotes.map((note, index) => {
                    const isSelected = selectedNoteIds.includes(note.id);
                    const isPinned = note.pinned;
                    const isArchived = note.archived;
                    const isDeleted = note.deleted;
                    const isProtected = note.is_protected === true;
                    const isUnlocked = isProtected && isNoteUnlocked(note.id);

                    // Show divider before archived section in favorites/tag views
                    const showArchivedDivider =
                      (viewMode === 'favorites' || selectedTagFilter) &&
                      index > 0 &&
                      !filteredAndSortedNotes[index - 1].archived &&
                      isArchived;

                    return (
                        <div key={note.id}>
                            {/* Archived Section Divider */}
                            {showArchivedDivider && (
                              <div className="px-4 py-2 text-xs text-muted-foreground/50 bg-muted/30 border-y border-border/50 flex items-center gap-2">
                                <div className="flex-1 h-px bg-border/50" />
                                <span className="font-medium">Archived</span>
                                <div className="flex-1 h-px bg-border/50" />
                              </div>
                            )}

                            <div className={cn(
                              "relative group",
                              isArchived && "opacity-60"
                            )} data-testid="note-card">
                                {/* Left border indicator for archived */}
                                {isArchived && (
                                  <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-500/30" />
                                )}

                                {/* Checkbox - Visible only in Selection Mode */}
                                {isSelectionMode && (
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center pointer-events-none">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            readOnly
                                            className="h-4 w-4 rounded border-border text-primary focus:ring-primary/20"
                                        />
                                    </div>
                                )}

                                <div
                                    draggable={!isSelectionMode}
                                    onDragStart={(e) => {
                                        // If note is selected and others are too, drag all selected
                                        const notesToDrag = selectedNoteIds.includes(note.id) && selectedNoteIds.length > 1
                                            ? selectedNoteIds
                                            : [note.id];

                                        const dragData = {
                                            type: 'note',
                                            noteIds: notesToDrag
                                        };

                                        e.dataTransfer.effectAllowed = 'copy';
                                        e.dataTransfer.setData('application/json', JSON.stringify(dragData));
                                        console.log('[NoteList] Drag started:', dragData);
                                    }}
                                    onClick={() => {
                                        if (isSelectionMode) {
                                            toggleNoteSelection(note.id);
                                        } else {
                                            setActiveNoteId(note.id);
                                        }
                                    }}
                                    className={cn(
                                        "w-full text-left p-4 hover:bg-muted/50 transition-all relative cursor-pointer",
                                        isSelectionMode && "pl-12", // Add padding for click area in selection mode
                                        !isSelectionMode && activeNoteId === note.id ? "bg-muted" : "",
                                        isSelectionMode && isSelected ? "bg-primary/5" : ""
                                    )}
                                >
                                    {!isSelectionMode && activeNoteId === note.id && (
                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                                    )}
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                          {/* Lock icon for protected notes */}
                                          {isProtected && (
                                            <span className={cn(
                                              "flex-shrink-0",
                                              isUnlocked ? "text-green-600" : "text-amber-600"
                                            )}>
                                              {isUnlocked ? <LockOpen size={12} /> : <Lock size={12} />}
                                            </span>
                                          )}
                                          <h3 className={cn("font-medium text-sm truncate", activeNoteId === note.id && !isSelectionMode ? "text-primary dark:text-foreground" : "text-foreground")}>
                                              {note.title || "Untitled Note"}
                                          </h3>
                                          {isArchived && (
                                            <span className="text-[9px] font-medium text-blue-600 bg-blue-100 dark:bg-blue-950/30 px-1.5 py-0.5 rounded-full flex-shrink-0">
                                              ARCHIVED
                                            </span>
                                          )}
                                          {note.note_type === 'skill' && (
                                            <span className="text-[9px] font-medium text-cyan-700 dark:text-cyan-400 bg-cyan-500/15 px-1.5 py-0.5 rounded-full flex-shrink-0 flex items-center gap-0.5">
                                              <Puzzle size={9} />
                                              SKILL
                                            </span>
                                          )}
                                        </div>

                                        {/* Right side: Unarchive + Archive + Pin + Version Badge together */}
                                        <div className="flex items-center gap-0.5 flex-shrink-0">
                                          {/* Unarchive button - only show for archived notes */}
                                          {!isSelectionMode && isArchived && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                unarchiveNote(note.id);
                                              }}
                                              className="p-1 rounded transition-all text-blue-600 opacity-0 group-hover:opacity-100 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                                              title="Unarchive note"
                                            >
                                              <ArchiveRestore size={12} />
                                            </button>
                                          )}

                                          {/* Archive button - only show on hover for non-archived notes */}
                                          {!isSelectionMode && !isArchived && viewMode !== 'archive' && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                archiveNote(note.id);
                                              }}
                                              className="p-1 rounded transition-all text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-accent"
                                              title="Archive note"
                                            >
                                              <Archive size={12} />
                                            </button>
                                          )}

                                          {/* Pin button - show on hover or when pinned (even for archived notes) */}
                                          {!isSelectionMode && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleTogglePin(note.id);
                                              }}
                                              className={cn(
                                                "p-1 rounded transition-all hover:bg-accent",
                                                isPinned
                                                  ? "text-yellow-600 opacity-100"
                                                  : "text-muted-foreground opacity-0 group-hover:opacity-100"
                                              )}
                                              title={isPinned ? "Unpin note" : "Pin note"}
                                            >
                                              <Pin size={12} fill={isPinned ? "currentColor" : "none"} />
                                            </button>
                                          )}

                                          {/* Version badge */}
                                          {note.snippets.length > 0 && (
                                            <span className="text-[10px] font-mono text-muted-foreground bg-muted-foreground/10 px-1.5 py-0.5 rounded flex-shrink-0">
                                              v{note.snippets[note.snippets.length - 1].version}
                                            </span>
                                          )}
                                        </div>
                                    </div>

                                    <p className={cn(
                                      "text-xs text-muted-foreground line-clamp-2 mb-2 h-8 font-normal",
                                      isProtected && !isUnlocked && "blur-sm select-none"
                                    )}>
                                        {isProtected && !isUnlocked
                                          ? "Protected content • Enter PIN to view"
                                          : note.snippets.length > 0
                                            ? note.snippets[note.snippets.length - 1].content
                                            : "No content"}
                                    </p>
                                    <div className="flex items-center justify-between gap-2">
                                        {/* Left: Time */}
                                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground/70">
                                          <Clock size={10} />
                                          <span>{formatDistanceToNow(new Date(note.updated_at), { addSuffix: true })}</span>
                                        </div>

                                        {/* Right: Tags (if any) — smart (amber), shared (blue), user (gray) */}
                                        {note.tags?.length > 0 && (
                                          <div className="flex items-center gap-1 flex-wrap">
                                            {smartTagsEnabled && note.tags.filter(t => isSmartTag(t)).slice(0, 2).map(tag => (
                                              <span key={tag} className="text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400">
                                                {tag.slice(SMART_TAG_PREFIX.length)}
                                              </span>
                                            ))}
                                            {note.tags.filter(t => !isSmartTag(t) && isTagShared(t)).slice(0, 2).map(tag => (
                                              <span key={tag} className="text-[9px] px-1 py-0.5 rounded bg-blue-500/15 text-blue-700 dark:text-blue-400">
                                                #{tag}
                                              </span>
                                            ))}
                                            {note.tags.filter(t => !isSmartTag(t) && !isTagShared(t)).slice(0, 2).map(tag => (
                                              <span key={tag} className="text-[9px] px-1 py-0.5 rounded bg-muted/50 text-muted-foreground">
                                                #{tag}
                                              </span>
                                            ))}
                                            {note.tags.length > 4 && (
                                              <span className="text-[9px] text-muted-foreground">
                                                +{note.tags.length - 4}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        ))}
      </div>

      {/* Bulk Actions Bar - Notes */}
      {selectedNoteIds.length > 0 && isSelectionMode && (
          <div className="absolute bottom-[calc(1rem+var(--sab))] left-4 right-4 bg-foreground text-background rounded-lg shadow-xl p-2 flex items-center justify-between z-30 animate-in slide-in-from-bottom-2 fade-in">
              <span className="text-xs font-medium pl-2">{selectedNoteIds.length} selected</span>
              <button
                onClick={handleBulkDelete}
                className="p-1.5 hover:bg-white/20 rounded-md transition-colors"
                title="Delete Selected"
              >
                  <Trash2 size={16} />
              </button>
          </div>
      )}

      {/* Report Modal for shared tag notes */}
      <ReportContentModal
        isOpen={!!reportNoteId}
        onClose={() => setReportNoteId(null)}
        contentType="shared_tag_note"
        contentId={reportNoteId || ''}
      />
    </div>
  );
}
